import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Page from 'src/app/components/Page';
import * as Yup from 'yup';
import { clearMassageErrorAction, Login } from '../../../features/authSlice';
import MsgError from '../../components/MsgError';
import ForgotPassword from './ForgotPass';
import { useTheme, useMediaQuery } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    overflow: 'hidden'
  },
  logo: {
    width: 67,
    height: 80,
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      width: 48,
      height: 52
    }
  },
  title: {
    fontWeight: 700,
    fontSize: 28,
    marginTop: 20,
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      marginTop: 12
    }
  },
  topGroup: {
    marginBottom: 32,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 14
    }
  },
  bottomGroup: {
    marginTop: 48,
    [theme.breakpoints.down('sm')]: {
      marginTop: 18
    }
  },
  bgWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%'
  },
  bgText: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    padding: 20,
    [theme.breakpoints.down('sm')]: {
      padding: 12,
      fontSize: 12,
      fontWeight: 500
    },
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    background: '#D9042F'
  }
}));

const formatLoginErrorMessage = failStatus => {
  const rawMessage = failStatus?.message || 'Đăng nhập thất bại vui lòng kiểm tra lại thông tin';
  return rawMessage;
};

const LoginView = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isLoadingLogin = useSelector(state => state.authSlice.isLoadingLogin);
  const failStatus = useSelector(state => state.authSlice.failStatus);
  const { isAccountant, isSale, isLeadMarketing, isTechnician } = useSelector(state => state.authSlice);

  const [showPassword, setShowPassword] = useState(false);
  const [forgotPass, setForgotPass] = useState(false);
  const [hideMsg, setHideMsg] = useState(false);

  const token = localStorage.getItem('access-token');

  const handleLogin = async values => {
    dispatch(clearMassageErrorAction());
    setHideMsg(false);

    try {
      await dispatch(Login(values)).unwrap();
    } catch {
      setHideMsg(false);
    }
  };

  useEffect(() => {
    if (token) {
      if (isAccountant || isSale || isLeadMarketing) {
        navigate('/app/manage/sales-orders', { replace: true });
      } else if (isTechnician) {
        navigate('/app/manage/devices', { replace: true });
      } else {
        navigate('/app/tracking', { replace: true });
      }
    }
  }, [token, isAccountant, isSale, isLeadMarketing, isTechnician]);

  return (
    <>
      {forgotPass && (
        <ForgotPassword open={forgotPass} setOpen={setForgotPass} />
      )}
      <Page className={classes.root} title="Đăng nhập">
        <Grid container style={{ minHeight: '100vh' }}>
          <Grid
            item
            xs={12}
            md={6}
            style={{ height: isMobile ? '60vh' : '100vh' }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              height="100%"
            >
              <Container maxWidth="sm">
                <Formik
                  initialValues={{ phone: '', password: '' }}
                  validationSchema={Yup.object().shape({
                    phone: Yup.string()
                      .matches(/^\S+$/, 'Tên đăng nhập không được chứa khoảng trắng')
                      .matches(
                        /^[a-zA-Z0-9_]+$/,
                        'Tên đăng nhập không được chứa ký tự đặc biệt'
                      )
                      .required('Tên đăng nhập là bắt buộc'),
                    password: Yup.string().required('Bạn cần nhập mật khẩu')
                  })}
                  onSubmit={handleLogin}
                >
                  {({
                    errors,
                    touched,
                    values,
                    handleBlur,
                    handleChange,
                    handleSubmit
                  }) => (
                    <form onSubmit={handleSubmit}>
                      <Box className={classes.topGroup}>
                        <img
                          src="/static/logo_login.svg"
                          alt="Logo"
                          className={classes.logo}
                        />
                        <Typography color="primary" className={classes.title}>
                          Đăng nhập
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        label="Tên đăng nhập (Số điện thoại)"
                        name="phone"
                        margin={isMobile ? 'dense' : 'normal'}
                        value={values.phone}
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                        onBlur={e => {
                          handleBlur(e);
                          setHideMsg(true);
                        }}
                        onChange={e => {
                          handleChange(e);
                          setHideMsg(true);
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Mật khẩu"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        margin={isMobile ? 'dense' : 'normal'}
                        value={values.password}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                        onBlur={e => {
                          handleBlur(e);
                          setHideMsg(true);
                        }}
                        onChange={e => {
                          handleChange(e);
                          setHideMsg(true);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(p => !p)}
                              >
                                {showPassword ? (
                                  <VisibilityOff style={{ color: '#D9042F' }} />
                                ) : (
                                  <Visibility style={{ color: '#D9042F' }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />

                      {!hideMsg && failStatus && (
                        <Typography color="error" align="center">
                          <MsgError content={formatLoginErrorMessage(failStatus)} />
                        </Typography>
                      )}

                      <Box className={classes.bottomGroup}>
                        <Box mb={1} textAlign="right">
                          <Typography
                            style={{
                              fontWeight: 700,
                              color: '#D9042F',
                              cursor: 'pointer'
                            }}
                            onClick={() => setForgotPass(true)}
                          >
                            Quên mật khẩu ?
                          </Typography>
                        </Box>

                        <Button
                          fullWidth
                          type="submit"
                          color="primary"
                          variant="contained"
                          size="large"
                          disabled={isLoadingLogin}
                          style={{ color: '#fff' }}
                        >
                          Đăng nhập
                          {isLoadingLogin && (
                            <CircularProgress
                              size={20}
                              color="secondary"
                              style={{ marginLeft: 16 }}
                            />
                          )}
                        </Button>

                        <Box mt={2}>
                          <Typography
                            style={{
                              color: '#BDBDBD',
                              fontSize: 14,
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            onClick={() => navigate('/privacy-policy')}
                          >
                            <i>Chính sách quyền riêng tư</i>
                          </Typography>
                        </Box>
                      </Box>
                    </form>
                  )}
                </Formik>
              </Container>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            style={{ height: isMobile ? '40vh' : '100vh' }}
          >
            <Box className={classes.bgWrapper}>
              <img
                src="/static/bg_login.jpeg"
                alt="Background"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div className={classes.bgText}>
                HỆ THỐNG GIÁM SÁT HÀNH TRÌNH VGPS
              </div>
            </Box>
          </Grid>
        </Grid>
      </Page>
    </>
  );
};

export default LoginView;
